import { OnInit, Component, DestroyRef, inject, SimpleChanges } from '@angular/core';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';
import {
  StandAreaMaintenanceDto,
  StandAreaMaintenanceCreateDto,
  StandAreaMaintenanceUpdateDto,
} from '../proxy/geographic/dtos/stand-area-maintenance';
import { STANDAREAMAINTENANCE_CONFIG, STANDAREAMAINTENANCE_FIELDS } from './stand-area-maintenance.config';
import {
  ActionTypes,
  BaseHierarchicalCrudComponent,
  createLocalizationKey,
  CrudPage,
  GenericForm,
  IActionModal,
  IActionsSchema,
  ICrudService,
  IActionTrigger,
  resolveActions,
  ZendraLocalizationServices,
} from '@zendra/shared';
import { StandAreaMaintenanceService } from '../proxy/geographic/controllers';
import { StandService } from 'cbd-proxies';
import { StandAreaMaintenancePlanningOrchestratorService } from 'orchestrator';
import {
  GetStandAreaMaintenanceProjectionListInputDto,
  StandAreaMaintenanceProjectionDto,
  StandAreaMaintenanceProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/stand-area-maintenances/models';

@Component({
  selector: 'app-stand-area-maintenance',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, StandAreaMaintenanceService, ConfirmationService],
  templateUrl: './stand-area-maintenance.html',
  styleUrl: './stand-area-maintenance.scss',
})
export class StandAreaMaintenance extends BaseHierarchicalCrudComponent<
  StandAreaMaintenanceProjectionDto,
  StandAreaMaintenanceCreateDto,
  StandAreaMaintenanceUpdateDto,
  StandAreaMaintenanceProjectionExcelDownloadDto
> implements OnInit {
  private resolvedActionContextKey: string | null = null;
  private readonly standNameLocalizationKey = createLocalizationKey(
    'Entity.StandAreaMaintenance.StandName',
    ZendraLocalizationServices.Geographic,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly standService = inject(StandService);
  private readonly orchestratorService = inject(StandAreaMaintenancePlanningOrchestratorService);

  private readonly geographicService = inject(StandAreaMaintenanceService);

  service: ICrudService<
    StandAreaMaintenanceProjectionDto,
    StandAreaMaintenanceCreateDto,
    StandAreaMaintenanceUpdateDto,
    StandAreaMaintenanceProjectionExcelDownloadDto
  > = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.geographicService.update(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'standAreaMaintenanceId',
    ),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetStandAreaMaintenanceProjectionListInputDto) =>
      this.orchestratorService.getList(query),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
  };
  config = STANDAREAMAINTENANCE_CONFIG;
  fields = STANDAREAMAINTENANCE_FIELDS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    this.resolveAvailableActions();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes['parentId'] || changes['isUsedInHierarchy']) {
      this.resolveAvailableActions();
    }
  }

  private resolveAvailableActions(): void {
    if (!this.config.actions || !this.config.actionIds) {
      this.actions = null;
      this.resolvedActionContextKey = null;
      return;
    }

    const contextKey = `${this.parentId}`;
    if (this.resolvedActionContextKey === contextKey) {
      return;
    }

    this.resolvedActionContextKey = contextKey;

    const actionCallbacks = {
      [this.config.actionIds.deleteStandAreaMaintenance]:
        () => this.deleteStandAreaMaintenance(),
      [this.config.actionIds.updateAreaTrigger]:
        () => this.updateArea(),
      [this.config.actionIds.undoAreaUpdateTrigger]:
        () => this.undoAreaUpdate(),
    };

    if (!this.isUsedInHierarchy || this.parentId === undefined) {
      this.actions = this.withGridRefreshOnActionCompleted(
        resolveActions(
          {
            singleRecord: this.config.actions.singleRecord,
            multipleRecord: this.config.actions.multipleRecord,
          },
          actionCallbacks,
        ),
      );
      return;
    }

    this.standService
      .getList({
        locationId: [this.parentId],
        maxResultCount: 1,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const stand = response.items?.[0];
          const parentStandId = stand?.id ?? null;
          const parentStandLocationId =
            stand?.locationId ?? stand?.location?.id ?? null;
          const parentStandName =
            stand?.location?.name?.trim() ?? stand?.name?.trim() ?? '';
          this.actions = this.withGridRefreshOnActionCompleted(
            resolveActions(
              this.buildActionsWithParentContext(
                parentStandLocationId ?? this.parentId!,
                parentStandId,
                parentStandName,
              ),
              actionCallbacks,
            ),
          );
        },
        error: () => {
          this.actions = this.withGridRefreshOnActionCompleted(
            resolveActions(
              this.buildActionsWithParentContext(this.parentId!, null, ''),
              actionCallbacks,
            ),
          );
        },
      });
  }

  private buildActionsWithParentContext(
    parentStandLocationId: number,
    parentStandId: number | null,
    parentStandName: string,
  ): IActionsSchema {
    const createActionId = this.config.actionIds.createStandAreaMaintenance;
    const updateAreaActionId = this.config.actionIds.updateAreaTrigger;

    return {
      overTable: this.config.actions.overTable?.map(action => {
        if (action.id === createActionId && action.actionType === ActionTypes.Modal) {
          const modalAction = action as IActionModal;
          return {
            ...modalAction,
            config: {
              ...modalAction.config,
              afterClosed: result => this.waitForProjectionConsistency(result),
              asideConfig: {
                ...modalAction.config.asideConfig,
                data: parentStandName
                  ? { [this.standNameLocalizationKey]: parentStandName }
                  : {},
              },
              inputs: {
                ...modalAction.config.inputs,
                parentStandLocationId,
                parentStandId,
                parentStandName,
              },
            },
          };
        }

        if (action.id === updateAreaActionId && action.actionType === ActionTypes.Trigger) {
          const triggerAction = action as IActionTrigger;
          return {
            ...triggerAction,
            afterSuccess: result => this.waitForProjectionConsistency(result),
          };
        }

        return action;
      }),
      multipleRecord: this.config.actions.multipleRecord,
      singleRecord: this.config.actions.singleRecord,
    };
  }

  protected prepareUpdateDto(
    data: any,
    selected: StandAreaMaintenanceProjectionDto,
  ): StandAreaMaintenanceUpdateDto {
    return {
      currentArea: data.currentArea,
      gisArea: data.gisArea,
      areaToBeAdjusted: data.areaToBeAdjusted,
      differenceInPercentage: data.differenceInPercentage,
      isAreaUpdated: data.isAreaUpdated,
      notes: data.notes,
      periodId: data.periodId,
      adjustmentReasonId: data.adjustmentReasonId,
      rotationId: data.rotationId,
      standLocationId: data.standLocationId,
      standAreaMaintenanceTypeId: data.standAreaMaintenanceTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'StandAreaMaintenance';
  }

  private getSelectedStandAreaMaintenanceId(): number | null {
    if (this.selectedRows.length !== 1) {
      return null;
    }

    const [standAreaMaintenanceId] = this.selectedRows;

    return standAreaMaintenanceId ?? null;
  }

  private deleteStandAreaMaintenance(): void {
    const standAreaMaintenanceId = this.getSelectedStandAreaMaintenanceId();

    if (standAreaMaintenanceId === null) {
      return;
    }

    this.loading.set(true);
    this.orchestratorService.delete(standAreaMaintenanceId).subscribe({
      next: () => {
        this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
          life: 5000,
          messageLocalizationParams: [this.localizationService.instant('Geographic::Action.Trigger.DeleteStandAreaMaintenance')],
        });
        this.selectedRows = [];
        this.refreshGrid();
      },
      error: err => {
        this.loading.set(false);
        console.error(err);
      },
    });
  }

  private updateArea(): Observable<StandAreaMaintenanceDto> | void {
    const standAreaMaintenanceId = this.getSelectedStandAreaMaintenanceId();

    if (standAreaMaintenanceId === null) {
      return;
    }

    return this.orchestratorService.updateArea(standAreaMaintenanceId).pipe(
      tap(() => {
        this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
          life: 5000,
          messageLocalizationParams: [this.localizationService.instant('Geographic::Action.Trigger.UpdateArea')],
        });
      }),
    );
  }

  private undoAreaUpdate(): void {
    this.runBulkAction(ids =>
      this.orchestratorService.undoAreas({ ids })
    );
  }
}
